import React, { useCallback, useId, useState } from 'react';
import useControllableState from '@/hooks/useControllableState';
import { classnames } from '@/utils/classnames/classnames';
import { useTranslations } from 'use-intl';
import { MAX_QUANTITY } from '@/constants/quantity';
import Input from '../input';
import { Props } from './types';

// EWM-428 (absorbed from the former quantity-stepper molecule) — block `e`/`E`
// (scientific notation) and `+`/`-` (sign) keystrokes in the numeric input; the
// +/- step buttons are the only way to change the value by one.
const BLOCKED_KEYS = ['e', 'E', '+', '-'];

// EWM-616 / EWM-926 — the solid-primary stepper chrome, shared by the PDP
// selector and the PLP product tile. It is factored out so the two variants are
// DERIVED from one set of values instead of hand-copied: round 1 of EWM-926
// forked `tile` off the outline-gray `default` tokens, the two surfaces drifted,
// and that drift is exactly what was reported. Anything changed here reaches
// both surfaces.
const PRIMARY_STEPPER_WRAPPER = 'border-primary items-center overflow-hidden border';
const PRIMARY_STEPPER_BTN =
  'bg-primary text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30';
const PRIMARY_STEPPER_INPUT =
  'border-primary text-14 text-primary border-y bg-white text-center font-semibold focus:outline-none';

// EWM-616 — PDP quantity selector. Same solid-blue look as `primary`, but with
// SQUARE buttons (size-10 = 40px, matching the input height) and a fixed-width
// input so it stays compact instead of stretching to ~50% of the PDP info
// column. The width is set via inline style because the shared Input atom
// applies `w-full`, which a `w-*` class can't override (classnames has no
// tailwind-merge). Scoped to the PDP so the cart / purchase-list steppers
// (which use `primary`) are unaffected.
const PDP_TOKENS = {
  wrapper: `inline-flex rounded-lg ${PRIMARY_STEPPER_WRAPPER}`,
  btn: PRIMARY_STEPPER_BTN,
  btnSize: 'flex size-10 items-center justify-center p-0',
  btnDecrementExtra: '',
  btnIncrementExtra: '',
  form: 'flex',
  input: `h-10 ${PRIMARY_STEPPER_INPUT}`,
  inputStyle: () => ({ width: '3rem' }) as React.CSSProperties,
} as const;

const VARIANT_TOKENS = {
  default: {
    wrapper: 'flex overflow-hidden rounded-md border border-gray-300 text-gray-700',
    btn: 'border-gray-300 bg-white transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:hover:bg-white',
    btnSize: 'flex size-[40px] items-center justify-center p-0',
    btnDecrementExtra: 'border-r',
    btnIncrementExtra: 'border-l',
    form: 'flex min-w-[40px] items-center justify-center p-0',
    input: 'text-14 mx-1 w-auto p-0 text-center',
    inputStyle: (rawValue: string) =>
      ({ width: `${Math.max(2, rawValue.length)}ch`, maxWidth: '5ch' }) as React.CSSProperties,
  },
  // Cart / purchase-list / quick-order stepper. PR #1190 review (suggestion 3) —
  // this was the THIRD hand-copied literal of the solid-primary chrome, left
  // behind by the very refactor that removed the other two. Its resolved class
  // SET is unchanged (a spec asserts that against the literal it replaced); all
  // that is gone is the third copy that would have drifted next.
  primary: {
    wrapper: `inline-flex rounded-lg ${PRIMARY_STEPPER_WRAPPER}`,
    btn: `px-3 py-2 ${PRIMARY_STEPPER_BTN}`,
    btnSize: '',
    btnDecrementExtra: '',
    btnIncrementExtra: '',
    form: 'flex',
    input: `w-12 py-2 ${PRIMARY_STEPPER_INPUT}`,
    inputStyle: () => undefined,
  },
  pdp: PDP_TOKENS,
  // EWM-926 — PLP product-tile quantity selector. Round 2: it is the `pdp`
  // selector, spread into a fresh object (so `pdp` itself can never be mutated)
  // with only the LAYOUT properties the tile genuinely needs overridden. Colours,
  // the disabled treatment and the input styling are inherited, so a future PDP
  // restyle carries to the PLP on its own.
  tile: {
    ...PDP_TOKENS,
    // Two deliberate departures from `pdp`: the stepper is FLUID (it shares a row
    // with the fixed-size Add to Purchase List button and must fill the rest of
    // it), and it keeps the tile's 4px `rounded` rather than the PDP's 8px
    // `rounded-lg` — the Add to Cart and Add to Purchase List buttons it sits
    // directly against are 4px by Dan's v0 card spec (EWM-317), so matching the
    // PDP here would make the tile internally inconsistent. Approved deviation.
    wrapper: `flex w-full rounded ${PRIMARY_STEPPER_WRAPPER}`,
    // Button height matches the tile's 41px Add to Cart / Purchase List controls
    // so the row aligns. Narrow +/- buttons below `sm`: in the PLP's 2-column
    // mobile grid the tile's action row is only ~137px wide, and two 41px buttons
    // plus the 42px Purchase List button left the input 3px (0px at 360px) — the
    // quantity was invisible. 32px here keeps the digits readable; `sm:` restores
    // the full 41px square.
    btnSize: 'flex h-[41px] w-8 shrink-0 items-center justify-center p-0 sm:w-[41px]',
    // A hard floor on the input so it can never be squeezed out of existence.
    form: 'flex min-w-[1.5rem] flex-1 items-center justify-center p-0',
    // Left to the shared Input atom's own `w-full` (no inline width override,
    // unlike `pdp`) so only the input absorbs the row's slack. `p-0` is
    // load-bearing, not tidiness: @tailwindcss/forms puts `padding: .5rem .75rem`
    // on every untyped input, and where `pdp` can afford 12px a side inside its
    // fixed 3rem box, the tile's input is squeezed to its 1.5rem floor in the
    // 2-column mobile grid — 24px of padding in a 24px box leaves a 0px content
    // box and the digit disappears (measured at 390px and 414px).
    input: `h-[41px] min-w-0 p-0 ${PRIMARY_STEPPER_INPUT}`,
    inputStyle: () => undefined,
  },
} as const;

const QuantityWidget = ({
  value: valueProp,
  defaultValue = 0,
  minValue = 0,
  maxValue = Number.MAX_VALUE,
  onChange,
  disabled,
  showLabel = true,
  spinbutton = false,
  variant = 'default',
}: Props) => {
  const translate = useTranslations();

  // EWM-801 — Chrome's "a form field element should have an id or name
  // attribute" fires on the cart page because this input had neither. It has to
  // be useId(), not a literal: the cart renders one widget PER LINE, so a fixed
  // id would emit duplicate ids — invalid HTML, and a worse problem than the
  // warning it silences.
  const inputId = useId();

  // EWM-442 — enforce the hard quantity ceiling on top of any stock-driven
  // `maxValue` a consumer passes (so a higher/absent stock cap still can't
  // exceed MAX_QUANTITY).
  const max = Math.min(maxValue, MAX_QUANTITY);

  const [value, setValue] = useControllableState(valueProp, Math.min(defaultValue, max));
  const [rawValue, setRawValue] = useState(value.toString());

  // EWM-760 — `rawValue` (the editable text the user is typing) is reset to
  // mirror `value` (the committed numeric value) whenever `value` changes
  // by any means: +/- buttons, a submitted edit, OR an external controlled
  // `value` prop. Adjust it during render (rather than a mount-of-cascading
  // `useEffect`) so the reset lands in the same render `value` changed in —
  // same trigger condition as the old `useEffect(() => …, [value])`, one
  // render sooner.
  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    setRawValue(value.toString());
  }

  const handleDecrement = useCallback(() => {
    const newValue = Math.max(minValue, value - 1);
    setValue(newValue);
    onChange?.(newValue);
  }, [value, minValue, setValue, onChange]);

  const handleIncrement = useCallback(() => {
    const newValue = Math.min(max, value + 1);
    setValue(newValue);
    onChange?.(newValue);
  }, [value, max, setValue, onChange]);

  // EWM-428 — onChange is the single funnel every input method passes through
  // (typing, paste, drag-drop, autofill), so strip to digits here; this rejects
  // negatives and scientific notation regardless of source.
  const handleRawValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRawValue(e.target.value.replace(/\D/g, ''));
  }, []);

  // Block the keys at source too, so `-`/`e` can't blank the field mid-edit.
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (BLOCKED_KEYS.includes(e.key)) e.preventDefault();
  }, []);

  const handleRawValueSubmit = useCallback(() => {
    const number = Math.max(minValue, Math.min(max, +rawValue));
    setValue(number);
    onChange?.(number);
    setRawValue(number.toString());
  }, [rawValue, minValue, max, onChange, setValue]);

  const spinbuttonProps = spinbutton
    ? {
        role: 'spinbutton' as const,
        'aria-valuenow': value,
        'aria-valuemin': minValue,
        'aria-valuemax': max,
      }
    : {};

  const t = VARIANT_TOKENS[variant];

  return (
    // EWM-926 — the `tile` variant fills its parent (see VARIANT_TOKENS.tile);
    // every other variant keeps the original content-sized root.
    <div className={classnames('flex items-center gap-3', { 'w-full': variant === 'tile' })}>
      {showLabel && <span className="text-14 text-gray-700">{translate('common.quantity-shorthand')}</span>}
      <div className={t.wrapper}>
        <button
          type="button"
          disabled={value <= minValue || disabled}
          className={classnames(t.btnSize, t.btn, t.btnDecrementExtra)}
          // PR #1190 review (suggestion 1) — the button's whole accessible name
          // was the glyph `-`, which a screen reader can announce as nothing.
          // The copy already existed in `en.json` with no consumer.
          aria-label={translate('common.decrease-quantity')}
          onClick={handleDecrement}
        >
          -
        </button>
        <form
          className={t.form}
          onSubmit={(e) => {
            e.preventDefault();
            handleRawValueSubmit();
          }}
        >
          <Input
            data-testid="quantity-widget-input"
            id={inputId}
            name="quantity"
            unStyled
            inputMode="numeric"
            className={t.input}
            style={t.inputStyle(rawValue)}
            aria-label={translate('common.quantity')}
            value={rawValue}
            onChange={handleRawValueChange}
            onKeyDown={handleKeyDown}
            onBlur={handleRawValueSubmit}
            disabled={disabled}
            {...spinbuttonProps}
          />
        </form>
        <button
          type="button"
          disabled={value >= max || disabled}
          className={classnames(t.btnSize, t.btn, t.btnIncrementExtra)}
          aria-label={translate('common.increase-quantity')}
          onClick={handleIncrement}
        >
          +
        </button>
      </div>
    </div>
  );
};

export default QuantityWidget;
